import { getProvider, clearProviderCache } from './chainUtils';
import { getChainConfig } from '../constants/chains';

// Network health check
export const checkNetworkHealth = async (chainId, isTestnet = false) => {
  try {
    const provider = getProvider(chainId, isTestnet);
    
    switch (chainId) {
      case 'TON': {
        // Use a simple balance check instead of getInfo() which might not exist
        const testAddress = 'EQD4FPq-PRDieyQKkizFTRtSDyucUIqrj0v_zXJmqaDp6_0t';
        const balance = await provider.getBalance(testAddress);
        return {
          isHealthy: balance !== null,
          blockHeight: null, // Not available without getInfo()
          timestamp: null,
          latency: null
        };
      }
      case 'BNB': {
        const [blockNumber, network] = await Promise.all([
          provider.getBlockNumber(),
          provider.getNetwork()
        ]);
        return {
          isHealthy: true,
          blockHeight: blockNumber,
          chainId: network.chainId,
          name: network.name
        };
      }
      default:
        throw new Error('Unsupported chain');
    }
  } catch (error) {
    console.error('Network health check failed:', error);
    return {
      isHealthy: false,
      error: error.message
    };
  }
};

// Provider connection management
export const reconnectProvider = async (chainId, isTestnet = false) => {
  try {
    clearProviderCache(chainId, isTestnet);
    const provider = getProvider(chainId, isTestnet);
    
    // Test provider connection
    switch (chainId) {
      case 'TON':
        // Use a simple balance check instead of getInfo()
        const testAddress = 'EQD4FPq-PRDieyQKkizFTRtSDyucUIqrj0v_zXJmqaDp6_0t';
        await provider.getBalance(testAddress);
        break;
      case 'BNB':
        await provider.getNetwork();
        break;
      default:
        throw new Error('Unsupported chain');
    }
    
    return true;
  } catch (error) {
    console.error('Provider reconnection failed:', error);
    return false;
  }
};

// Chain configuration validation
export const validateChainConfig = (chainId) => {
  try {
    const config = getChainConfig(chainId);
    if (!config) {
      return {
        isValid: false,
        error: 'Chain configuration not found'
      };
    }

    const requiredFields = [
      'name',
      'symbol',
      'decimals',
      'testnet.rpcUrl',
      'mainnet.rpcUrl',
      'features'
    ];

    const missingFields = requiredFields.filter(field => {
      const fields = field.split('.');
      let value = config;
      for (const f of fields) {
        value = value?.[f];
        if (!value) return true;
      }
      return false;
    });

    return {
      isValid: missingFields.length === 0,
      missingFields: missingFields.length > 0 ? missingFields : undefined
    };
  } catch (error) {
    console.error('Chain config validation failed:', error);
    return {
      isValid: false,
      error: error.message
    };
  }
};

// Transaction monitoring
export const monitorTransaction = async (txHash, chainId, options = {}) => {
  const {
    confirmations = 1,
    timeout = 300000, // 5 minutes
    pollInterval = 5000 // 5 seconds
  } = options;

  try {
    const provider = getProvider(chainId);
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      switch (chainId) {
        case 'TON': {
          const status = await provider.getTransactionStatus(txHash);
          if (status.isFinalized) {
            return {
              success: true,
              status,
              confirmations: status.confirmations
            };
          }
          break;
        }
        case 'BNB': {
          const receipt = await provider.getTransactionReceipt(txHash);
          if (receipt && receipt.confirmations >= confirmations) {
            return {
              success: true,
              receipt,
              confirmations: receipt.confirmations
            };
          }
          break;
        }
        default:
          throw new Error('Unsupported chain');
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    throw new Error('Transaction monitoring timed out');
  } catch (error) {
    console.error('Transaction monitoring failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Error handling and recovery
export const handleChainError = async (error, chainId, isTestnet = false) => {
  console.error('Chain error:', error);

  const errorType = categorizeError(error);
  switch (errorType) {
    case 'NETWORK':
      return await reconnectProvider(chainId, isTestnet);
    case 'PROVIDER':
      clearProviderCache(chainId, isTestnet);
      return await reconnectProvider(chainId, isTestnet);
    case 'CONFIG':
      return validateChainConfig(chainId);
    default:
      return {
        handled: false,
        error: error.message
      };
  }
};

// Helper function to categorize errors
const categorizeError = (error) => {
  const message = error.message.toLowerCase();
  
  if (message.includes('network') || message.includes('connection')) {
    return 'NETWORK';
  }
  if (message.includes('provider')) {
    return 'PROVIDER';
  }
  if (message.includes('config') || message.includes('configuration')) {
    return 'CONFIG';
  }
  return 'UNKNOWN';
}; 